import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of, zip } from 'rxjs';
import { CommunicationsApiService } from '../store/communications/communications-api.service';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { FieldDataType, FilterGroup, FilterGroupType, QueryParams, RuleOperator, TableFilter } from '../../core/store/models';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import * as fromSecurityGroup from 'src/app/modules/management/store/security-groups';
import { Store } from '@ngrx/store';
import { UserApiService } from '../../management/store/users/user-api.service';
import {
  MassRecipientType,
  MassNotificationSelectedRecipient,
  ToRecipientSettings,
  DatasetSelectedRecipientFilter,
  DistributionListCount,
  DocumentsRefRelatedDocumentsRequest
} from '../store/communications/communications.model';
import { DataApiService } from '../../management/store/data/data-api.service';
import { DatasetApiService } from '../../core/services/dataset-api.service';
import { Dataset } from '../../core/store/models/dataset.model';

@Injectable({
  providedIn: 'root'
})
export class RecipientSelectionService implements OnDestroy {
  constructor(private datasetApiService: DatasetApiService, private commApiService: CommunicationsApiService) {}

  private datasetCache: { [uId: string]: Dataset } = {};

  public init() {
    this.datasetCache = {};
  }

  public getFilterGroupFromTableFilter(filter: TableFilter): FilterGroup {
    if (!!filter) {
      const filterGroup = {
        groupType: FilterGroupType.And,
        filters: [],
        filterGroups: []
      } as FilterGroup;

      filter.filters?.forEach(f => {
        const childFilterGroup = {
          groupType: FilterGroupType.Or,
          filters: [],
          filterGroups: []
        } as FilterGroup;

        f.values?.forEach(v => {
          childFilterGroup.filters.push({
            field: f.systemName,
            operator: RuleOperator.Equal,
            value: v.uId
          });
        });

        if (childFilterGroup.filterGroups?.length > 0 || childFilterGroup.filters?.length > 0) {
          filterGroup.filterGroups.push(childFilterGroup);
        }
      });

      if (filterGroup.filterGroups?.length > 0 || filterGroup.filters?.length > 0) {
        return filterGroup;
      }
    }

    return null;
  }

  public getDatasetDocumentsRelated(datasetInfo: DatasetSelectedRecipientFilter, countsOnly: boolean): Observable<any> {
    const request = {
      refFieldname: null,
      queryMatchRule: null,
      documentUIds: datasetInfo.selectedUIds,
      totalCountOnly: true,
      countsOnly: countsOnly
    } as DocumentsRefRelatedDocumentsRequest;
    return this.commApiService.getDocumentsRefRelatedDocuments(datasetInfo.datasetUId, request).pipe(
      map(res => {
        return res;
      })
    );
  }

  public getTableFilterDisplay(recipientFilter: DatasetSelectedRecipientFilter, dataset?: Dataset): Observable<string> {
    if (!!recipientFilter) {
      if (!!!dataset) {
        if (!!this.datasetCache[recipientFilter.datasetUId]) {
          return of(this.getDisplayForTableFilter(recipientFilter.filter, this.datasetCache[recipientFilter.datasetUId]));
        }
        return this.datasetApiService.getDataset(recipientFilter.datasetUId, { includeWorkflow: false }).pipe(
          catchError(e => {
            console.error(e);
            return of(null);
          }),
          map(res => {
            if (!!res) {
              this.datasetCache[recipientFilter.datasetUId] = res;
              return this.getDisplayForTableFilter(recipientFilter.filter, res);
            }
            return null;
          })
        );
      } else {
        return of(this.getDisplayForTableFilter(recipientFilter.filter, dataset));
      }
    } else {
      return of('');
    }
  }

  public getDistListMembersCount(recipients: MassNotificationSelectedRecipient[]): Observable<DistributionListCount[]> {
    const distRecipients = [];
    recipients
      ?.filter(r => r.type === MassRecipientType.DistributionList)
      ?.forEach(r => {
        distRecipients.push(r.uId);
      });
    if (distRecipients.length) {
      return this.commApiService.getDistributionListMembersCount(distRecipients).pipe(
        map(res => {
          return res;
        })
      );
    } else {
      return of([]);
    }
  }

  private getDisplayForTableFilter(filter: TableFilter, dataset: Dataset): string {
    let display = '';
    const andTerms = [];
    filter?.filters?.forEach(f => {
      const field = dataset?.schema?.fields?.find(fld => fld.systemName === f.systemName);

      if (!!field) {
        const values = [];
        f.values.forEach(v => {
          values.push(`${field.displayName}=${v.name}`);
        });

        if (values.length > 1) {
          andTerms.push('(' + values.join(' OR ') + ')');
        } else {
          andTerms.push(values.join(' OR '));
        }
      }
    });

    if (andTerms.length > 1) {
      display = '(' + andTerms.join(' AND ') + ')';
    } else {
      display = andTerms.join(' AND ');
    }
    return display;
  }

  ngOnDestroy() {}
}
